import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Link as LinkIcon, Unlink, Mail, RefreshCw, ChevronRight, X } from 'lucide-react';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { useUpgradeFlow } from '../../hooks/useUpgradeFlow';
import { useTranslation } from '../../i18n/hooks/useTranslation';

interface OutlookStatus {
  connected: boolean;
  email?: string;
  lastSyncAt?: string;
  expiresAt?: string;
  emailCounts?: {
    total: number;
    withContacts: number;
    clients: number;
    suppliers: number;
    staff: number;
    unmatched: number;
  };
}

export const OutlookIntegration: React.FC = () => {
  const { t } = useTranslation();
  const { openUpgradeFlow } = useUpgradeFlow();
  const [status, setStatus] = useState<OutlookStatus>({ connected: false });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${apiUrl}/api/outlook/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setStatus(data.data);
      } else {
        setError(data.message || 'Failed to fetch Outlook status');
      }
    } catch (err) {
      console.error('Fetch Outlook status error:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setError(null);
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${apiUrl}/api/outlook/connect`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        const popup = window.open(
          data.data.authorizationUrl,
          'OutlookAuth',
          `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
        );

        const pollTimer = setInterval(() => {
          if (popup && popup.closed) {
            clearInterval(pollTimer);
            setLoading(false);
            fetchStatus();
          }
        }, 500);

        const messageHandler = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          
          if (event.data.type === 'OUTLOOK_AUTH_SUCCESS') {
            clearInterval(pollTimer);
            if (popup) popup.close();
            setSuccess('Outlook connected successfully!');
            fetchStatus();
            window.removeEventListener('message', messageHandler);
          } else if (event.data.type === 'OUTLOOK_LIMIT_ERROR') {
            clearInterval(pollTimer);
            if (popup) popup.close();
            setLoading(false);
            window.removeEventListener('message', messageHandler);
            // Open upgrade modal with limit details
            openUpgradeFlow({
              limitType: event.data.limit?.type || 'integrations',
              limitDetails: event.data.limit
            });
          } else if (event.data.type === 'OUTLOOK_AUTH_ERROR') {
            clearInterval(pollTimer);
            if (popup) popup.close();
            setError(event.data.message || 'Failed to connect Outlook');
            setLoading(false);
            window.removeEventListener('message', messageHandler);
          }
        };

        window.addEventListener('message', messageHandler);
      } else {
        setError(data.message || 'Failed to initiate Outlook connection');
        setLoading(false);
      }
    } catch (err) {
      console.error('Connect Outlook error:', err);
      setError('Failed to connect to Outlook');
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setError(null);
      setLoading(true);
      setShowDisconnectConfirm(false);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${apiUrl}/api/outlook/disconnect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess('Outlook disconnected successfully');
        setStatus({ connected: false });
      } else {
        setError(data.message || 'Failed to disconnect Outlook');
      }
    } catch (err) {
      console.error('Disconnect Outlook error:', err);
      setError('Failed to disconnect from Outlook');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncEmails = async () => {
    try {
      setError(null);
      setSyncing(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('You must be logged in to sync emails');
        setSyncing(false);
        return;
      }
      
      const response = await fetch(`${apiUrl}/api/outlook/sync-emails`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ syncAllContacts: true })
      });

      const data = await response.json();
      
      if (response.status === 401) {
        setError('Authentication failed. Please try logging out and back in.');
        return;
      }
      
      if (response.status === 403) {
        setError('You do not have permission to sync emails. Contact your administrator.');
        return;
      }
      
      if (data.success) {
        const contactsCount = data.data?.contactsCount;
        const message = contactsCount 
          ? `Outlook sync started for ${contactsCount} contacts. Each contact will be synced individually to update People and email threads.`
          : (data.message || 'Emails synced successfully!');
        setSuccess(message);
        fetchStatus();
      } else {
        setError(data.message || 'Failed to sync emails');
      }
    } catch (err) {
      console.error('Sync emails error:', err);
      setError('Failed to sync emails from Outlook');
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  return (
    <>
      <ConfirmDialog
        isOpen={showDisconnectConfirm}
        title={t('admin.disconnectOutlook')}
        message={t('admin.disconnectOutlookMessage')}
        confirmLabel={t('admin.disconnect')}
        cancelLabel={t('common.cancel')}
        type="danger"
        onConfirm={handleDisconnect}
        onCancel={() => setShowDisconnectConfirm(false)}
      />

      {/* Compact Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6 h-full flex flex-col">
        <div className="mb-4">
          <div className="flex items-start mb-3">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 transition-colors ${
              status.connected 
                ? 'bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50' 
                : 'bg-gray-100 dark:bg-gray-800'
            }`}>
              <Mail className="w-8 h-8 text-accent-500" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
                Outlook Integration
              </h3>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed flex-1">
                  {t('admin.outlookDescription')}
                </p>
                {status.connected ? (
                  <div className="flex items-center text-success-600 dark:text-success-400 flex-shrink-0">
                    <CheckCircle className="w-4 h-4 mr-1.5" />
                    <span className="font-medium text-sm">{t('admin.connected')}</span>
                  </div>
                ) : (
                  <div className="flex items-center text-gray-500 dark:text-gray-400 flex-shrink-0">
                    <XCircle className="w-4 h-4 mr-1.5" />
                    <span className="font-medium text-sm">{t('admin.notConnected')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-gray-700 dark:border dark:border-red-800/50 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-800 dark:text-error-400">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-gray-700 dark:border dark:border-green-800/50 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-sm text-green-800 dark:text-success-400">{success}</p>
          </div>
        )}

        <div className="mt-auto pt-4">
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500 mx-auto"></div>
            </div>
          ) : status.connected ? (
            <div className="flex gap-2">
              <button
                onClick={() => setShowDisconnectConfirm(true)}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-error-500 text-white rounded-lg hover:bg-error-600 transition-colors font-medium text-sm"
              >
                {t('admin.disconnect')}
              </button>
              <button
                onClick={() => setShowDetailModal(true)}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors text-gray-700 dark:text-gray-300 font-medium text-sm"
              >
{t('admin.info')}
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleConnect}
                disabled={loading}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium text-sm"
              >
                <LinkIcon className="w-4 h-4 mr-2" />
                {t('admin.connect')}
              </button>
              <button
                onClick={() => setShowDetailModal(true)}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors text-gray-700 dark:text-gray-300 font-medium text-sm"
              >
{t('admin.info')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setShowDetailModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('admin.outlookDetails')}</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {status.connected ? (
                <>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('admin.email')}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {status.email || 'Unknown'}
                  </p>
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('admin.tokenExpires')}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {formatDate(status.expiresAt)}
                  </p>
                </div>
              </div>

              {status.emailCounts && status.emailCounts.total > 0 && (
                <div className="bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50 border border-accent-200 dark:border-accent-800 rounded-lg p-4">
                  <p className="text-sm font-semibold text-accent-900 dark:text-accent-100 mb-3">
                    Synced Email Threads:
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-accent-600 dark:text-accent-400">
                        {status.emailCounts.total}
                      </p>
                      <p className="text-xs text-accent-700 dark:text-accent-300">Total</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-accent-600 dark:text-accent-400">
                        {status.emailCounts.clients}
                      </p>
                      <p className="text-xs text-accent-700 dark:text-accent-300">Clients</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-accent-600 dark:text-accent-400">
                        {status.emailCounts.suppliers}
                      </p>
                      <p className="text-xs text-accent-700 dark:text-accent-300">Suppliers</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-accent-600 dark:text-accent-400">
                        {status.emailCounts.staff}
                      </p>
                      <p className="text-xs text-accent-700 dark:text-accent-300">Staff</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                        {status.emailCounts.unmatched}
                      </p>
                      <p className="text-xs text-gray-700 dark:text-gray-400">Other</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-600">
                <button
                  onClick={handleSyncEmails}
                  disabled={syncing}
                  className="flex-1 flex items-center justify-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? t('admin.syncing') : t('admin.syncEmails')}
                </button>

                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setShowDisconnectConfirm(true);
                  }}
                  disabled={loading}
                  className="flex items-center justify-center px-4 py-2 bg-error-500 text-white rounded-lg hover:bg-error-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  <Unlink className="w-4 h-4 mr-2" />
                  {t('admin.disconnect')}
                </button>
              </div>
                </>
              ) : (
                <>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      {t('admin.outlookNoIntegration')}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('admin.outlookNoIntegrationHint')}
                    </p>
                  </div>
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
                    <button
                      onClick={() => {
                        setShowDetailModal(false);
                        handleConnect();
                      }}
                      disabled={loading}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                    >
                      <LinkIcon className="w-4 h-4" />
                      {t('admin.connect')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

